import { GlobalIcon } from "get-life-storybook-ts";
import { trackPaymentMethodSelected } from "../../../../../../helpers/Tracking";

export type WalletType = "card" | "apple_pay" | "google_pay";

interface PaymentMethodPickerProps {
  selected: WalletType;
  onChange: (m: WalletType) => void;
  translate: (key: string) => string;
  googlePayAvailable: boolean;
  applePayAvailable: boolean;
}

const VisaMark = () => (
  <span className="paymentV3MethodMark paymentV3MethodMark--visa" aria-label="Visa">
    VISA
  </span>
);

const MasterMark = () => (
  <span className="paymentV3MethodMark paymentV3MethodMark--master" aria-label="Mastercard">
    <span className="paymentV3MethodMark__mcDot paymentV3MethodMark__mcDot--red" />
    <span className="paymentV3MethodMark__mcDot paymentV3MethodMark__mcDot--orange" />
  </span>
);

const AmexMark = () => (
  <span className="paymentV3MethodMark paymentV3MethodMark--amex" aria-label="American Express">
    AMEX
  </span>
);

const ApplePayMark = () => (
  <span className="paymentV3MethodMark paymentV3MethodMark--apple" aria-label="Apple Pay">
    <svg width="9" height="11" viewBox="0 0 17 21" fill="white" aria-hidden>
      <path d="M14.7 11.1c0-2.7 2.2-4 2.3-4.1-1.3-1.8-3.2-2.1-3.9-2.1-1.7-.2-3.2 1-4.1 1-.9 0-2.2-1-3.6-.9-1.8 0-3.6 1.1-4.5 2.7-1.9 3.4-.5 8.3 1.4 11 .9 1.3 2 2.8 3.4 2.7 1.4-.1 1.9-.9 3.5-.9 1.7 0 2.1.9 3.5.9 1.5 0 2.4-1.3 3.3-2.7 1-1.5 1.5-3 1.5-3.1-.1 0-2.8-1.1-2.8-4.5zm-2.7-8.3C12.7 1.9 13.2.7 13 -.5c-1 .1-2.3.7-3 1.6-.7.8-1.3 2-1.1 3.2 1.2.1 2.4-.6 3.1-1.5z" />
    </svg>
    <span>Pay</span>
  </span>
);

const GooglePayMark = () => (
  <span className="paymentV3MethodMark paymentV3MethodMark--google" aria-label="Google Pay">
    <span style={{ color: "#4285F4" }}>G</span>
    <span style={{ color: "#EA4335" }}>o</span>
    <span style={{ color: "#FBBC04" }}>o</span>
    <span style={{ color: "#4285F4" }}>g</span>
    <span style={{ color: "#34A853" }}>l</span>
    <span style={{ color: "#EA4335" }}>e</span>
    <span style={{ marginLeft: 2, color: "#424242" }}>Pay</span>
  </span>
);

const PaymentMethodPicker = ({
  selected,
  onChange,
  translate,
  googlePayAvailable,
  applePayAvailable,
}: PaymentMethodPickerProps) => {
  const options: Array<{
    id: WalletType;
    label: string;
    marks: JSX.Element;
    disabled?: boolean;
    disabledReason?: string;
  }> = [
    {
      id: "card",
      label: translate("paymentV3.method.card"),
      marks: (
        <span className="paymentV3MethodPicker__marks">
          <VisaMark />
          <MasterMark />
          <AmexMark />
        </span>
      ),
    },
  ];

  if (applePayAvailable) {
    options.push({
      id: "apple_pay",
      label: translate("paymentV3.method.applePay"),
      marks: (
        <span className="paymentV3MethodPicker__marks">
          <ApplePayMark />
        </span>
      ),
    });
  }

  if (googlePayAvailable) {
    options.push({
      id: "google_pay",
      label: translate("paymentV3.method.googlePay"),
      marks: (
        <span className="paymentV3MethodPicker__marks">
          <GooglePayMark />
        </span>
      ),
    });
  }

  return (
    <div className="paymentV3MethodPicker" role="radiogroup">
      {options.map((opt) => {
        const isSelected = selected === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-disabled={opt.disabled || false}
            disabled={opt.disabled}
            onClick={() => {
              if (opt.disabled) return;
              if (opt.id !== selected) trackPaymentMethodSelected(opt.id);
              onChange(opt.id);
            }}
            className={`paymentV3MethodPicker__option ${
              isSelected ? "paymentV3MethodPicker__option--active" : ""
            } ${opt.disabled ? "paymentV3MethodPicker__option--disabled" : ""}`}
          >
            <span className="paymentV3MethodPicker__label">{opt.label}</span>
            {opt.marks}
            {opt.disabled && opt.disabledReason && (
              <span className="paymentV3MethodPicker__pill">
                {opt.disabledReason}
              </span>
            )}
            {isSelected && !opt.disabled && (
              <span className="paymentV3MethodPicker__check">
                <GlobalIcon iconName="CheckedIcon" size="XS" color="#FFFFFF" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default PaymentMethodPicker;
